import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Grid, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { BreakdownChart, TimeSeriesChart } from "../../components/dashboard/AnalyticsCharts";
import ModuleLayout from "../../components/enterprise/ModuleLayout";
import RiskScoreCard from "../../components/enterprise/RiskScoreCard";
import { platformMockService } from "../../services/mock/platformMockService";
import { analyticsTabs } from "../navigation";
import { useTranslation } from "react-i18next";
import EmptyState from "../../components/dashboard/EmptyState";
import { usePlatform } from "../../context/PlatformContext";
import saasApi from "../../utils/saasApi";
import { useSharedFilters, forEndpoint } from "../../hooks/useSharedFilters";

const RealLossPrevention = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { filters, setFilter, pathFor } = useSharedFilters("detectionAnalytics");
  const { branch_id, camera_id, status, severity, date_from, date_to } = filters;
  const analyticsQuery = useMemo(() => ({ ...forEndpoint({ branch_id, camera_id, status, severity, date_from, date_to }, "detectionAnalytics"), detection_type: "shoplifting" }), [
    branch_id, camera_id, status, severity, date_from, date_to,
  ]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    saasApi.getDetectionAnalytics(analyticsQuery)
      .then((result) => { if (live) { setData(result); setError(''); } })
      .catch((err) => { if (live) { setData(null); setError(err.message); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [analyticsQuery]);
  const change = (field) => (event) => setFilter(field, event.target.value);
  const options = data?.filters || { cameras: [], branches: [] };
  return <Stack spacing={2}>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
      <TextField size="small" type="date" label={t('analyticsLive.from')} InputLabelProps={{ shrink: true }} value={filters.date_from} onChange={change('date_from')} />
      <TextField size="small" type="date" label={t('analyticsLive.to')} InputLabelProps={{ shrink: true }} value={filters.date_to} onChange={change('date_to')} />
      <TextField size="small" select label={t('analyticsLive.branch')} value={filters.branch_id} onChange={change('branch_id')} sx={{ minWidth: 150 }}>
        <MenuItem value="">{t('analyticsLive.all')}</MenuItem>{options.branches.map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>)}
      </TextField>
      <TextField size="small" select label={t('analyticsLive.camera')} value={filters.camera_id} onChange={change('camera_id')} sx={{ minWidth: 150 }}>
        <MenuItem value="">{t('analyticsLive.all')}</MenuItem>{options.cameras.map((camera) => <MenuItem key={camera.id} value={camera.id}>{camera.name}</MenuItem>)}
      </TextField>
      <TextField size="small" select label={t('analyticsLive.severity')} value={filters.severity} onChange={change('severity')} sx={{ minWidth: 140 }}>
        <MenuItem value="">{t('analyticsLive.all')}</MenuItem>{['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <TextField size="small" select label={t('analyticsLive.status')} value={filters.status} onChange={change('status')} sx={{ minWidth: 150 }}>
        <MenuItem value="">{t('analyticsLive.all')}</MenuItem>{['nueva', 'en_revision', 'confirmada', 'falsa_alarma', 'resuelta'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
    </Stack>
    {error && <Alert severity="error">{error}</Alert>}
    {loading && <Typography>{t('analyticsLive.loading')}</Typography>}
    {!loading && data && <>
      <Grid container spacing={1.5}>{[
        [t('analyticsLive.alerts'), data.summary.total],
        [t('analyticsLive.resolved'), data.summary.resolved],
        [t('analyticsLive.activeCameras'), data.summary.cameras],
      ].map(([label, value]) => <Grid item xs={12} sm={4} key={label}><Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}><Typography color="text.secondary" variant="body2">{label}</Typography><Typography variant="h5">{value}</Typography></Paper></Grid>)}</Grid>
      {data.summary.total === 0 ? <EmptyState title={t('analyticsLive.empty')} /> : <>
        <TimeSeriesChart rows={data.by_day} period={{ starts_on: data.period?.date_from, ends_on: data.period?.date_to }} dateKey="date" countKey="total" unit={t('analyticsLive.alerts').toLowerCase()} locale={i18n.language} selectLabel={t('analyticsLive.from')} emptyLabel={t('analyticsLive.empty')} onSelectDay={(day) => navigate(pathFor('/incidents', { detection_type: 'shoplifting', date_from: day, date_to: day }))} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}><Typography variant="subtitle1">{t('analyticsLive.severity')}</Typography><BreakdownChart rows={data.by_severity} total={data.summary.total} getKey={(row) => row.label} getLabel={(row) => row.label} getCount={(row) => row.total} getHref={(row) => pathFor('/incidents', { detection_type: 'shoplifting', severity: row.label })} locale={i18n.language} unit={t('analyticsLive.alerts').toLowerCase()} emptyLabel={t('analyticsLive.empty')} /></Grid>
          <Grid item xs={12} md={4}><Typography variant="subtitle1">{t('analyticsLive.status')}</Typography><BreakdownChart rows={data.by_status} total={data.summary.total} getKey={(row) => row.label} getLabel={(row) => row.label} getCount={(row) => row.total} getHref={(row) => pathFor('/incidents', { detection_type: 'shoplifting', status: row.label })} locale={i18n.language} unit={t('analyticsLive.alerts').toLowerCase()} emptyLabel={t('analyticsLive.empty')} /></Grid>
          <Grid item xs={12} md={4}><Typography variant="subtitle1">{t('analyticsLive.byCamera')}</Typography><BreakdownChart rows={data.by_camera} total={data.summary.total} getKey={(row) => row.camera_id || 'none'} getLabel={(row) => row.name} getCount={(row) => row.total} getHref={(row) => row.camera_id ? pathFor('/incidents', { detection_type: 'shoplifting', camera_id: String(row.camera_id) }) : null} locale={i18n.language} unit={t('analyticsLive.alerts').toLowerCase()} emptyLabel={t('analyticsLive.empty')} /></Grid>
        </Grid>
      </>}
    </>}
  </Stack>;
};

const LossPreventionPage = () => {
  const { t } = useTranslation();
  const { showMockData } = usePlatform();
  return (
    <ModuleLayout title={t("platform.pages.lossPrevention.title")} subtitle={t("platform.pages.lossPrevention.subtitle")} breadcrumbs={[t("platform.nav.analytics"), t("platform.tabs.lossPrevention")]} tabs={analyticsTabs} dataMode={{ mode: showMockData ? "mock" : "real" }}>
      {showMockData ? (
        <Grid container spacing={2.5}>
          {platformMockService.getRiskScores().map((item) => (
            <Grid item xs={12} md={4} key={item.scope}><RiskScoreCard item={item} /></Grid>
          ))}
        </Grid>
      ) : (
        <RealLossPrevention />
      )}
    </ModuleLayout>
  );
};

export default LossPreventionPage;
